import { useEffect, useRef } from "react";
import type { ReactNode, Dispatch, SetStateAction } from "react";
import { useTranslation } from "next-i18next";

import { BasicHeader } from "./BasicHeader/BasicHeader";
import { BasicRow } from "./BasicRow/BasicRow";
import { SelectHeader } from "./SelectHeader/SelectHeader";
import { SelectRow } from "./SelectRow/SelectRow";
import { Checkbox } from "common/ui/Checkbox";
import { Skeleton } from "common/ui/Skeleton";

import styles from "./Table.module.scss";

interface Props {
  headers: React.ReactNode[],
  rows?: {
    id?: string | number,
    onClick?: () => void,
    items: ReactNode[],
    disabled?: boolean,
  }[],
  selected?: (string | number)[],
  setSelected?: Dispatch<SetStateAction<(string | number)[]>>,
  pageSize?: string,
}

export function Table({
  headers,
  rows,
  selected,
  setSelected,
  pageSize,
}: Props) {
  const { t } = useTranslation("common");
  const headerCheckbox = useRef<HTMLInputElement>(null);

  const filteredHeaders = headers.filter(h => (
    h !== undefined
    && h !== null
    && h !== false
  ));

  const headerCheckboxOnChangeHandler = () => {
    if (selected && setSelected && rows) {
      if (selected.length > 0) {
        setSelected([]);
      } else {
        setSelected(prev => {
          const newSelected = rows
            .filter(row => !row.disabled)
            .map((row) => {
              if (!prev.includes(row.id)) {
                return row.id;
              }
            });

          return [...prev, ...newSelected];
        });
      }
    }
  };

  const rowCheckboxOnChangeHandler = (id: string) => {
    if (setSelected) {
      setSelected((prev: (string | number)[]) => {
        const ids = [...prev];

        if (ids.includes(id)) {
          ids.splice(ids.indexOf(id), 1);
          return ids;
        }

        return ids.concat(id);
      })
    }
  };

  useEffect(() => {
    if (headerCheckbox.current && selected) {
      if (rows?.length === selected.length) {
        headerCheckbox.current.indeterminate = false;
      } else if (selected.length > 0) {
        headerCheckbox.current.indeterminate = true;
      } else {
        headerCheckbox.current.indeterminate = false;
      }
    }
  }, [headerCheckbox.current, rows, selected]);

  return (
    <div className={styles.Table}>
      <table>
        <thead>
          {selected ? (
            <SelectHeader
              headers={filteredHeaders}
              selected={selected}
            >
              <Checkbox
                ref={headerCheckbox}
                checked={rows?.length > 0 && rows?.length === selected.length}
                onChange={headerCheckboxOnChangeHandler}
              />
            </SelectHeader>
          ) : (
            <BasicHeader headers={filteredHeaders} />
          )}
        </thead>
        <tbody>
          {rows ? (
            rows.length > 0 ? (
              rows.map((row, i) => (
                row ? (
                  selected ? (
                    <SelectRow
                      key={i}
                      row={row}
                      isSelected={selected.includes(row.id as string)}
                    >
                      <Checkbox
                        checked={selected.includes(row.id as string)}
                        onChange={() => rowCheckboxOnChangeHandler(row.id as string)}
                        disabled={row.disabled}
                      />
                    </SelectRow>
                  ) : (
                    <BasicRow key={i} row={row} />
                  )
                ) : null
              ))
            ) : selected ? (
              <SelectRow row={{ items: [t("ui.table.noItems")] }}>
                <Checkbox disabled />
              </SelectRow>
            ) : (
              <BasicRow row={{ items: [t("ui.table.noItems")] }} />
            )
          ) : selected ? (
            [...Array(pageSize ? Number(pageSize) : 5)].map((i, idx) => (
              <SelectRow
                key={idx}
                row={{
                  items: [...Array(filteredHeaders.length)].map((i, idx) => (
                    <Skeleton key={idx} height={24} width={160} />
                  ))
                }}
              >
                <Checkbox checked={false} onChange={() => {}} />
              </SelectRow>
            ))
          ) : (
            [...Array(pageSize ? Number(pageSize) : 5)].map((i, idx) => (
              <BasicRow
                key={idx}
                row={{
                  items: [...Array(filteredHeaders.length)].map((i, idx) => (
                    <Skeleton key={idx} height={24} width={160} />
                  ))
                }}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

